import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { lenisRef } from "@/components/layout/SmoothScroll";

const HEATHUB_URL = "https://www.heathub.ie/book";
const DRAW = { duration: 1.3, ease: [0.65, 0, 0.35, 1] };
const btn = "group/btn inline-flex h-14 w-full items-center justify-center gap-3 px-6 font-display text-base font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-300 sm:w-auto";

const Line = ({ cls, axis, delay, duration = 1.1 }) => (
  <motion.span className={`absolute ${cls}`} initial={axis === "x" ? { scaleX: 0 } : { scaleY: 0 }} animate={axis === "x" ? { scaleX: 1 } : { scaleY: 1 }}
    transition={{ ...DRAW, duration, delay }} />
);

const Tee = () => (
  <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-16 md:block" aria-hidden data-testid="path-tee">
    <Line cls="left-0 top-2 h-0.5 w-[calc(25%-8px)] origin-left bg-leaf" axis="x" delay={0.7} />
    <Line cls="left-[calc(25%-9px)] top-2 h-14 w-0.5 origin-top bg-leaf" axis="y" delay={1.75} duration={0.5} />
    <Line cls="left-[calc(25%-8px)] top-2 h-0.5 w-[calc(50%+16px)] origin-left bg-brand" axis="x" delay={1.75} />
    <Line cls="left-[calc(75%+7px)] top-2 h-14 w-0.5 origin-top bg-brand" axis="y" delay={2.8} duration={0.5} />
    <span className="absolute left-[calc(25%-13px)] top-[3px] h-2.5 w-2.5 border-2 border-leaf bg-canvas" />
  </div>
);

const Card = ({ tone, label, title, text, children, testid, logo }) => (
  <div className={`group relative flex h-full flex-col border bg-canvas p-7 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(18,22,28,0.35)] sm:p-9 ${tone === "leaf" ? "border-canvas-line hover:border-leaf" : "border-canvas-line hover:border-brand"}`} data-testid={testid}>
    <span className={`absolute inset-x-0 top-0 h-1 ${tone === "leaf" ? "bg-leaf" : "bg-brand"}`} aria-hidden />
    <span className={`label-mono ${tone === "leaf" ? "text-leaf" : "text-brand"}`}>{label}</span>
    {logo}
    <h2 className="mt-5 font-display text-3xl font-bold uppercase leading-[0.95] sm:text-4xl">{title}</h2>
    <p className="mt-4 flex-1 text-steel">{text}</p>
    <div className="mt-8">{children}</div>
  </div>
);

const scrollToEnquiry = () => {
  const el = document.getElementById("enquiry");
  if (!el) return;
  if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -90 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const PathCards = () => (
  <div className="relative">
    <Tee />
    <div className="grid gap-5 md:grid-cols-2 md:gap-8 md:pt-16">
      <Reveal className="h-full">
        <Card tone="leaf" testid="path-homeowner" label="Path A — Homeowners" title="Book your Heat Pump or Gas Boiler service"
          logo={<img src="/img/heathub-logo.png" alt="HeatHub.ie" className="mt-6 h-12 w-auto self-start sm:h-14" data-testid="heathub-logo" />}
          text="Homeowner looking for a service? Book your heat pump or gas boiler service online with HeatHub.">
          <a href={HEATHUB_URL} target="_blank" rel="noopener noreferrer" className={`${btn} bg-leaf hover:bg-[#25620a]`} data-testid="heathub-book-btn">
            Book at www.heathub.ie
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
          </a>
          <p className="label-mono mt-3 text-steel">Opens www.heathub.ie in a new tab</p>
        </Card>
      </Reveal>
      <Reveal className="h-full" delay={0.1}>
        <Card tone="brand" testid="path-contractor" label="Path B — Developers & contractors" title="Planning a scheme? Send a project enquiry"
          text="Mechanical fit-out, heat pumps, ventilation and heating for housing, apartment and commercial schemes.">
          <button onClick={scrollToEnquiry} className={`${btn} bg-brand hover:bg-[#154fa6]`} data-testid="scroll-enquiry-btn">
            Send a project enquiry
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-y-0.5" />
          </button>
          <p className="label-mono mt-3 text-steel">Goes straight to enquiries@rrhp.ie</p>
        </Card>
      </Reveal>
    </div>
  </div>
);
